// src/pages/admin/AdminLad.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import exportAnalyticsPdfWithTemplate from '../utils/analyticsPdf';

const loadJsPDF = async () => {
  if (typeof window !== 'undefined') {
    const ctor = window.jspdf?.jsPDF || window.jsPDF;
    if (typeof ctor === 'function') return ctor;
  }
  try {
    const mod = await import('jspdf');
    return mod.jsPDF || mod.default;
  } catch (err) {
    console.error('[LAD] Failed to load jsPDF', err);
    return null;
  }
};

const exportAnalyticsPdfFallback = async ({ title, generatedAt, sections, fileName }) => {
  const jsPDFCtor = await loadJsPDF();
  if (typeof jsPDFCtor !== 'function') {
    throw new Error('PDF engine unavailable');
  }

  const doc = new jsPDFCtor({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const margin = 40;
  const lineHeight = 18;
  const usableWidth = doc.internal.pageSize.getWidth() - margin * 2;
  const pageHeight = doc.internal.pageSize.getHeight();
  let cursorY = margin;

  const ensureSpace = (height = lineHeight) => {
    if (cursorY + height > pageHeight - margin) {
      doc.addPage();
      cursorY = margin;
    }
  };

  const writeLines = (lines) => {
    const items = Array.isArray(lines) && lines.length ? lines : ['No data available.'];
    items.forEach((raw) => {
      if (raw === null || typeof raw === 'undefined') return;
      const text = String(raw);
      const chunks = doc.splitTextToSize(text, usableWidth);
      chunks.forEach((chunk) => {
        ensureSpace();
        doc.text(chunk, margin, cursorY);
        cursorY += lineHeight;
      });
    });
  };

  const addSection = ({ title: sectionTitle, lines }) => {
    if (sectionTitle) {
      ensureSpace();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(sectionTitle, margin, cursorY);
      cursorY += lineHeight;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    writeLines(lines);
    cursorY += lineHeight * 0.5;
  };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title || 'Learning Analytics Report', margin, cursorY);
  cursorY += lineHeight;

  const generatedLabel = generatedAt instanceof Date
    ? generatedAt.toLocaleString()
    : generatedAt || new Date().toLocaleString();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  writeLines([`Generated: ${generatedLabel}`]);
  cursorY += lineHeight * 0.5;

  const safeSections = Array.isArray(sections) ? sections : [];
  safeSections.forEach((section) => addSection(section || {}));
  doc.save(fileName || `learning-analytics-${new Date().toISOString().slice(0, 10)}.pdf`);
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const emptyAnalytics = {
  totalSessions: 0,
  completedSessions: 0,
  completedDurationSessions: 0,
  upcomingSessions: 0,
  totalMinutes: 0,
  averageMinutes: 0,
  uniquePartners: 0,
  partnerBreakdown: [],
  topSubjects: [],
  monthlyHours: [],
  monthOptions: [{ value: '', label: 'All months' }],
  upcoming: [],
  recent: [],
  totalStudentsAcross: 0,
};

const numberOrNA = (value) => (typeof value === 'number' ? value.toLocaleString() : 'N/A');

const buildAnalyticsReportSections = ({
  analytics,
  role,
  totalHours,
  averageDurationLabel,
  averageGroupSize,
  selectedUserOption,
  selectedUserDetails,
  userMap,
}) => {
  const sections = [];
  const roleLabel = role === 'tutor' ? 'Tutor' : 'Student';
  const selectedUserId = selectedUserDetails?._id || selectedUserOption?.value || 'N/A';
  const selectedUserName = selectedUserDetails?.name || selectedUserOption?.label || 'N/A';
  const selectedUserEmail = selectedUserDetails?.email || 'N/A';

  sections.push({
    title: 'Selected User',
    lines: selectedUserOption ? [
      `Role: ${roleLabel}`,
      `Name: ${selectedUserName}`,
      `Email: ${selectedUserEmail}`,
      `User ID: ${selectedUserId}`,
    ] : [
      `Role filter: ${roleLabel}`,
      'No user selected.',
    ],
  });

  const uniqueLabel = role === 'tutor' ? 'Unique Students' : 'Unique Tutors';
  const keyMetrics = [
    `Total Sessions: ${numberOrNA(analytics.totalSessions)}`,
    `Completed Sessions: ${numberOrNA(analytics.completedSessions)}`,
    `Upcoming Sessions: ${numberOrNA(analytics.upcomingSessions)}`,
    `Total Hours: ${Number.isFinite(totalHours) ? totalHours.toFixed(2) : 'N/A'}`,
    `Average Duration: ${averageDurationLabel}`,
    `${uniqueLabel}: ${numberOrNA(analytics.uniquePartners)}`,
    role === 'tutor' ? `Avg. Students per Session: ${averageGroupSize || 'N/A'}` : null,
    role === 'tutor' ? `Total Students Across Sessions: ${numberOrNA(analytics.totalStudentsAcross)}` : null,
  ].filter(Boolean);
  sections.push({ title: 'Key Metrics', lines: keyMetrics });

  const monthlyLines = Array.isArray(analytics.monthlyHours)
    ? analytics.monthlyHours.map((item) => {
      const label = item.label || item.month || 'Month';
      const value = Number.isFinite(item.hours) ? item.hours.toFixed(2) : '0.00';
      return `${label}: ${value} hrs`;
    })
    : [];
  sections.push({ title: 'Monthly Hours', lines: monthlyLines });

  const subjectLines = Array.isArray(analytics.topSubjects)
    ? analytics.topSubjects.map((item, index) => (
      `${index + 1}. ${(item.subject || 'Unknown')} - ${numberOrNA(item.count)}`
    ))
    : [];
  sections.push({ title: 'Top Subjects', lines: subjectLines });

  const partnerHeading = role === 'tutor' ? 'Student Breakdown' : 'Tutor Breakdown';
  const partnerLines = Array.isArray(analytics.partnerBreakdown)
    ? analytics.partnerBreakdown.map((item) => {
      const partner = userMap.get(item.id);
      const displayName = partner?.name || item.name || partner?.email || 'Unknown';
      const count = typeof item.count === 'number' ? item.count : 0;
      const suffix = count === 1 ? 'session' : 'sessions';
      return `${displayName}: ${count} ${suffix}`;
    })
    : [];
  sections.push({ title: partnerHeading, lines: partnerLines });

  const upcomingLines = Array.isArray(analytics.upcoming)
    ? analytics.upcoming.map(({ start, session }) => (
      `${formatDateTime(start)} - ${session?.subject || session?.title || 'Session'}`
    ))
    : [];
  sections.push({ title: 'Upcoming Sessions', lines: upcomingLines });

  const recentLines = Array.isArray(analytics.recent)
    ? analytics.recent.map(({ start, session }) => {
      const status = session?.ended || session?.status === 'ended' ? 'Completed' : 'In progress';
      return `${formatDateTime(start)} - ${session?.subject || session?.title || 'Session'} (${status})`;
    })
    : [];
  sections.push({ title: 'Recent Sessions', lines: recentLines });

  return sections;
};

const StatCard = ({ icon, label, value, helper }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 18,
            bgcolor: 'primary.50',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </Box>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
            {label}
          </Typography>
        </Stack>
        <Typography component="p" variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="body2" color="text.secondary">
            {helper}
          </Typography>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const AdminLearningAnalytics = () => {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const [role, setRole] = useState('tutor');
  const [selectedUser, setSelectedUser] = useState(null);
  const [analytics, setAnalytics] = useState(emptyAnalytics);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthOptions, setMonthOptions] = useState(emptyAnalytics.monthOptions);

  const exportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const loadSummary = async () => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const { data } = await api.get('/analytics/admin-summary');
        setSummary(data);
      } catch (err) {
        setSummaryError(err?.response?.data?.message || err.message || 'Failed to load analytics.');
      } finally {
        setSummaryLoading(false);
      }
    };
    loadSummary();
  }, []);

  const userMap = useMemo(() => {
    const map = new Map();
    userOptions.forEach((option) => {
      if (option?.user?._id) map.set(option.user._id, option.user);
      if (option?.value) map.set(option.value, option.user || {});
    });
    return map;
  }, [userOptions]);

  const loadUserOptions = useCallback(async (nextRole = role, q = '') => {
    setUsersLoading(true);
    try {
      const { data } = await api.get('/analytics/users', {
        params: { role: nextRole, q, limit: 25 },
      });
      setUserOptions(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error('Failed to load users for analytics');
      setUserOptions([]);
    } finally {
      setUsersLoading(false);
    }
  }, [role]);

  useEffect(() => {
    loadUserOptions(role, '');
  }, [loadUserOptions, role]);

  const handleRoleChange = (_, nextRole) => {
    if (!nextRole || nextRole === role) return;
    setRole(nextRole);
    setSelectedUser(null);
    setAnalytics(emptyAnalytics);
    setSelectedMonth(null);
    setMonthOptions(emptyAnalytics.monthOptions);
    setUserOptions([]);
  };

  const loadUserAnalytics = useCallback(async (option, monthOption = selectedMonth) => {
    setAnalytics(emptyAnalytics);
    if (!option) return;
    setSessionsLoading(true);
    try {
      const { data } = await api.get(`/analytics/users/${role}/${option.value}/summary`, {
        params: monthOption?.value ? { month: monthOption.value } : undefined,
      });
      const nextAnalytics = { ...emptyAnalytics, ...(data || {}) };
      setAnalytics(nextAnalytics);
      const nextMonthOptions = Array.isArray(nextAnalytics.monthOptions) && nextAnalytics.monthOptions.length
        ? nextAnalytics.monthOptions
        : emptyAnalytics.monthOptions;
      setMonthOptions(nextMonthOptions);
      if (!monthOption) setSelectedMonth(nextMonthOptions[0]);

      if (!nextAnalytics.totalSessions) {
        toast.info('No sessions found for the selected user');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setSessionsLoading(false);
    }
  }, [role, selectedMonth]);

  const handleUserSelect = (_, option) => {
    setSelectedUser(option || null);
    setSelectedMonth(null);
    setMonthOptions(emptyAnalytics.monthOptions);
    loadUserAnalytics(option || null, null);
  };

  const handleMonthSelect = (_, option) => {
    const nextMonth = option || monthOptions[0] || emptyAnalytics.monthOptions[0];
    setSelectedMonth(nextMonth);
    if (selectedUser) loadUserAnalytics(selectedUser, nextMonth);
  };

  const totalHours = Number(((analytics.totalMinutes || 0) / 60).toFixed(2));
  const averageDurationLabel = analytics.averageMinutes ? `${analytics.averageMinutes.toFixed(1)} min` : 'N/A';
  const averageGroupSize = role === 'tutor' && analytics.totalSessions
    ? (analytics.totalStudentsAcross / analytics.totalSessions).toFixed(1)
    : null;

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    if (!exportRef.current) {
      toast.error('Select a user to generate the analytics PDF.');
      setExporting(false);
      return;
    }
    const generatedAt = new Date();
    const reportTitle = 'Learning Journey';
    const fileName = `learning-analytics-${generatedAt.toISOString().slice(0, 10)}.pdf`;
    const contextRole = role === 'tutor' ? 'tutor' : 'student';
    const selectedUserDetails = selectedUser
      ? (selectedUser.user || userMap.get(selectedUser.value) || null)
      : null;
    const sections = buildAnalyticsReportSections({
      analytics,
      role: contextRole,
      totalHours,
      averageDurationLabel,
      averageGroupSize,
      selectedUserOption: selectedUser,
      selectedUserDetails,
      userMap,
    });

    const exportUser =
      selectedUserDetails ||
      selectedUser?.user ||
      (selectedUser
        ? {
            name: (selectedUser.label || '').replace(/\s*\([^)]*\)\s*$/, '').trim() || selectedUser.label,
            email: (() => {
              const match = /\(([^)]+)\)/.exec(selectedUser.label || '');
              return match ? match[1] : '';
            })(),
            profilePictureUrl: selectedUser?.user?.profilePictureUrl || selectedUser?.user?.avatarUrl || null,
          }
        : null);

    try {
      try {
        await exportAnalyticsPdfWithTemplate({
          rootNode: exportRef.current,
          title: reportTitle,
          selectedUser: exportUser,
          generatedAt,
          fileName,
        });
      } catch (templateErr) {
        console.warn('[LAD] DOM capture PDF export failed, using fallback layout', templateErr);
        await exportAnalyticsPdfFallback({
          title: reportTitle,
          generatedAt,
          sections,
          fileName,
        });
      }
    } catch (err) {
      console.error('[LAD] PDF export failed', err);
      toast.error('Failed to generate analytics PDF.');
    } finally {
      setExporting(false);
    }
  }, [analytics, averageDurationLabel, averageGroupSize, exporting, role, selectedUser, totalHours, userMap, exportRef]);

  const summaryCards = summary ? [
    {
      label: 'Students',
      value: numberOrNA(summary.studentCount),
      icon: <SchoolRoundedIcon fontSize="small" />,
    },
    {
      label: 'Tutors',
      value: numberOrNA(summary.tutorCount),
      icon: <AssignmentIndRoundedIcon fontSize="small" />,
    },
    {
      label: 'Sessions Scheduled',
      value: numberOrNA(summary.totalSessions),
      icon: <QueryStatsRoundedIcon fontSize="small" />,
    },
    {
      label: 'Upcoming Sessions',
      value: numberOrNA(summary.upcomingSessions),
      icon: <TimelineRoundedIcon fontSize="small" />,
    },
  ] : [];

  const totalSubjectsCount = analytics.topSubjects.reduce((acc, item) => acc + (item.count || 0), 0) || 1;

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            title={
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Chip label="Live" color="success" size="small" />
              </Stack>
            }
            subheader="Monitor performance across the platform and drill into tutor or student metrics."
            action={
              <Tooltip title="Export the current analytics view to PDF">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={exporting ? <CircularProgress size={18} /> : <DownloadRoundedIcon />}
                    onClick={handleExport}
                    disabled={exporting}
                  >
                    {exporting ? 'Preparing...' : 'Export PDF'}
                  </Button>
                </span>
              </Tooltip>
            }
          />
          {summaryLoading && <LinearProgress />}
          {summaryError && !summaryLoading ? (
            <CardContent>
              <Alert severity="error">{summaryError}</Alert>
            </CardContent>
          ) : (
            <CardContent>
              <Grid container spacing={2.5}>
                {summaryCards.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.label}>
                    <StatCard icon={card.icon} label={card.label} value={card.value} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader
            title="User Analytics"
            subheader="Select a role and user to explore individual performance metrics."
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              <ToggleButtonGroup
                value={role}
                exclusive
                onChange={handleRoleChange}
                size="small"
              >
                <ToggleButton value="tutor">Tutors</ToggleButton>
                <ToggleButton value="student">Students</ToggleButton>
              </ToggleButtonGroup>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Autocomplete
                  options={userOptions}
                  loading={usersLoading}
                  value={selectedUser}
                  onChange={handleUserSelect}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') loadUserOptions(role, value);
                  }}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  sx={{ flexGrow: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={role === 'tutor' ? 'Select a tutor' : 'Select a student'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Autocomplete
                  options={monthOptions}
                  value={selectedMonth}
                  onChange={handleMonthSelect}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  disabled={!monthOptions.length || sessionsLoading}
                  sx={{ minWidth: 220 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Month filter"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {sessionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>

              {sessionsLoading ? (
                <LinearProgress />
              ) : !selectedUser ? (
                <Alert severity="info">Select a user to see detailed analytics.</Alert>
              ) : !analytics.totalSessions ? (
                <Alert severity="warning">No sessions found for the selected user.</Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        {selectedUser && (
          <Box ref={exportRef} sx={{ mt: 1.5 }}>
            <Stack spacing={3}>
              <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<QueryStatsRoundedIcon fontSize="small" />}
                  label="Total Sessions"
                  value={numberOrNA(analytics.totalSessions)}
                  helper={`Completed: ${numberOrNA(analytics.completedSessions)}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<TimelineRoundedIcon fontSize="small" />}
                  label="Upcoming"
                  value={numberOrNA(analytics.upcomingSessions)}
                  helper="Next 4 sessions listed below"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<SchoolRoundedIcon fontSize="small" />}
                  label="Total Hours"
                  value={totalHours.toFixed(2)}
                  helper={`Average duration ${averageDurationLabel}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<GroupsRoundedIcon fontSize="small" />}
                  label={role === 'tutor' ? 'Unique Students' : 'Unique Tutors'}
                  value={numberOrNA(analytics.uniquePartners)}
                  helper={role === 'tutor' ? `Avg. group size ${averageGroupSize || 'N/A'}` : undefined}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title="Monthly Study Hours" subheader="Hours tracked per calendar month" />
                  <CardContent>
                    {analytics.monthlyHours.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No data available.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Month</TableCell>
                            <TableCell align="right">Hours</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analytics.monthlyHours.map((row) => (
                            <TableRow key={row.month}>
                              <TableCell>{row.label || row.month}</TableCell>
                              <TableCell align="right">{Number(row.hours || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title="Top Subjects" subheader="Most attended subjects for the selected user" />
                  <CardContent>
                    {analytics.topSubjects.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No subject data available.
                      </Typography>
                    ) : (
                      <List dense>
                        {analytics.topSubjects.map((subject) => {
                          const share = subject.count && totalSubjectsCount
                            ? Math.round((subject.count / totalSubjectsCount) * 100)
                            : 0;
                          return (
                            <ListItem key={subject.subject} sx={{ px: 0 }}>
                              <ListItemText
                                primary={subject.subject || 'Unknown'}
                                secondary={`${subject.count} sessions - ${share}%`}
                              />
                              <Box sx={{ width: 120, ml: 2, bgcolor: 'grey.200', height: 6, borderRadius: 999 }}>
                                <Box sx={{ width: `${Math.min(share, 100)}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 999 }} />
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title={role === 'tutor' ? 'Student Breakdown' : 'Tutor Breakdown'} />
                  <CardContent>
                    {analytics.partnerBreakdown.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No partner data available.
                      </Typography>
                    ) : (
                      <List dense>
                        {analytics.partnerBreakdown.map((partner) => (
                          <ListItem key={partner.id} sx={{ px: 0 }}>
                            <ListItemText
                              primary={partner.name || 'Unknown'}
                              secondary={`${partner.count} ${partner.count === 1 ? 'session' : 'sessions'}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title="Upcoming Sessions" subheader="Next four sessions" />
                  <CardContent>
                    {analytics.upcoming.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No upcoming sessions.
                      </Typography>
                    ) : (
                      <List dense>
                        {analytics.upcoming.map(({ start, session }) => (
                          <ListItem key={session._id || `${start}-${session.subject}`} sx={{ px: 0 }}>
                            <ListItemText
                              primary={session.subject || session.title || 'Session'}
                              secondary={formatDateTime(start)}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card variant="outlined">
              <CardHeader title="Recent Sessions" subheader="Latest five sessions for the selected user" />
              <CardContent>
                {analytics.recent.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No recent session activity.
                  </Typography>
                ) : (
                  <List dense>
                    {analytics.recent.map(({ start, session }) => {
                      const ended = session?.ended || session?.status === 'ended';
                      return (
                        <ListItem key={session._id || `${start}-${session.subject}`} sx={{ px: 0 }}>
                          <ListItemText
                            primary={session.subject || session.title || 'Session'}
                            secondary={`${formatDateTime(start)} - ${ended ? 'Completed' : 'In progress'}`}
                          />
                          {ended ? <Chip size="small" label="Done" color="success" /> : <Chip size="small" label="Live" color="info" />}
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>
            </Stack>
          </Box>
        )}
      </Stack>
    </DashboardLayout>
  );
};

export default AdminLearningAnalytics;
































